import { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import {
  Plus, RefreshCw, Mail as MailIcon, Pencil, Trash2, TestTube2, Loader2,
  CheckCircle2, AlertCircle, Users as UsersIcon, X, ListTree, Download,
} from 'lucide-react';
import { api } from '../lib/auth';
import { toast } from 'sonner';

/**
 * Super-admin only — IMAP account manager.
 *  • CRUD for accounts (host/port/SSL/credentials/sync interval)
 *  • One-click "Test Bağlantı" — verifies IMAP login + lists folders
 *  • "Sync Şimdi" — manual sync trigger
 *  • Permissions sub-dialog — grant per-user read access
 */
const EMPTY = {
  label: '',
  email_address: '',
  imap_host: '',
  imap_port: 993,
  imap_ssl: true,
  username: '',
  password: '',
  sync_interval_minutes: 5,
  active: true,
};

export default function MailAccountsSettings() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [permsAccount, setPermsAccount] = useState(null);
  const [progressAccount, setProgressAccount] = useState(null);

  const refresh = async () => {
    try {
      setLoading(true);
      const res = await api.get('/mail/accounts');
      setAccounts(res.data?.data || []);
    } catch {
      toast.error('Hesaplar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  // Şub 10, 2026 — Aktif sync'leri 3sn'de bir poll et (UI badge güncel kalsın)
  useEffect(() => {
    const anySyncing = accounts.some((a) => a.sync_in_progress);
    if (!anySyncing) return undefined;
    const intv = setInterval(async () => {
      try {
        const res = await api.get('/mail/accounts');
        setAccounts(res.data?.data || []);
      } catch { /* ignore */ }
    }, 3000);
    return () => clearInterval(intv);
  }, [accounts]);

  const cancelSync = async (acc) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Çalışan senkronizasyonu iptal etmek istediğinize emin misiniz?\n\nNot: Şu anda çekilen klasör tamamlanacak, sonraki klasöre geçmeden duracak. Bir sonraki çalıştırmada kaldığı yerden devam eder (zaten DB\'de olanları atlayarak).')) return;
    try {
      const res = await api.post(`/mail/accounts/${acc.id}/sync-cancel`);
      if (res.data?.data?.cancelled) {
        toast.success('İptal sinyali gönderildi — birazdan duracak');
        setTimeout(refresh, 4000);
      } else {
        toast.message(res.data?.data?.reason || 'İptal edilemedi');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İptal başarısız');
    }
  };

  const onAdd = () => {
    setEditId(null);
    setForm(EMPTY);
    setEditOpen(true);
  };
  const onEdit = (acc) => {
    setEditId(acc.id);
    setForm({ ...EMPTY, ...acc, password: '' });
    setEditOpen(true);
  };

  const submit = async () => {
    setBusy(true);
    try {
      if (editId) {
        const payload = { ...form };
        if (!payload.password) delete payload.password; // keep existing
        const res = await api.put(`/mail/accounts/${editId}`, payload);
        const rulesPaused = res.data?.deactivated_rules || 0;
        const pendingReact = res.data?.pending_reactivation || 0;
        if (rulesPaused > 0) {
          toast.success(`Hesap pasife alındı`, {
            description: `${rulesPaused} arşiv kuralı otomatik kapatıldı. Kural sayfasında "pasif · hesap pasif" etiketli görünecekler.`,
            duration: 8000,
          });
        } else if (pendingReact > 0) {
          toast.success('Hesap aktif edildi');
          // eslint-disable-next-line no-alert
          if (window.confirm(
            `Bu hesaba bağlı ${pendingReact} arşiv kuralı, hesap pasife alındığında otomatik kapatılmıştı.\n\nHepsini yeniden aktif etmek ister misiniz?`
          )) {
            try {
              const rr = await api.post(`/mail/accounts/${editId}/reactivate-rules`);
              toast.success(`${rr.data?.data?.reactivated || 0} kural yeniden aktif edildi`, { duration: 6000 });
            } catch {
              toast.error('Kurallar aktif edilemedi');
            }
          }
        } else if (payload.active === false) {
          // The operator turned the account off but no linked rules were
          // affected. Most common cause: every rule is "global" (applies
          // to ALL accounts). Give a hint so they know why nothing happened.
          toast.success('Hesap pasife alındı', {
            description: 'Hiçbir kural etkilenmedi — kurallarınız "tüm hesaplar" kapsamında olabilir. Sadece bu hesaba bağlı kurallar otomatik pasife alınır.',
            duration: 7000,
          });
        } else {
          toast.success('Güncellendi');
        }
      } else {
        await api.post('/mail/accounts', form);
        toast.success('Hesap eklendi');
      }
      setEditOpen(false);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kaydedilemedi');
    } finally {
      setBusy(false);
    }
  };

  const removeAcc = async (acc) => {
    if (!window.confirm(`"${acc.label}" hesabını ve tüm mesajlarını silmek istediğine emin misin?`)) return;
    try {
      await api.delete(`/mail/accounts/${acc.id}`);
      toast.success('Silindi');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Silinemedi');
    }
  };

  const testConn = async (acc) => {
    try {
      const res = await api.post(`/mail/accounts/${acc.id}/test`);
      const folders = res.data?.data?.folders || [];
      toast.success(`Bağlantı başarılı — ${folders.length} klasör bulundu`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bağlantı hatası');
    }
  };

  const syncNow = async (acc) => {
    try {
      const t = toast.loading('Senkronizasyon arka planda başlatılıyor...');
      const res = await api.post(`/mail/accounts/${acc.id}/sync`);
      toast.dismiss(t);
      if (res.data?.success) {
        toast.success('Senkron arka planda çalışıyor — biraz sonra mail kutusunu kontrol edin', { duration: 5000 });
      } else {
        toast.error('Sync başlatılamadı');
      }
      // Background — 5sn sonra refresh ile listeyi güncelle
      setTimeout(refresh, 5000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync başarısız');
    }
  };

  // Şub 10, 2026 — Full sync akışı: önce preview (sayım), kullanıcı onaylarsa tam çek.
  const fullSync = async (acc) => {
    let previewData;
    const previewToast = toast.loading('Sunucudan klasör/mail sayıları alınıyor...');
    try {
      const r = await api.get(`/mail/accounts/${acc.id}/sync-preview`);
      previewData = r.data?.data;
      toast.dismiss(previewToast);
    } catch (err) {
      toast.dismiss(previewToast);
      toast.error(err.response?.data?.detail || 'Önizleme alınamadı');
      return;
    }
    if (!previewData) return;
    const folderLines = (previewData.folders || [])
      .filter((f) => f.server_total > 0)
      .slice(0, 10)
      .map((f) => `• ${f.name}: sunucu=${f.server_total}, DB=${f.in_db}, çekilecek≈${f.to_fetch}`)
      .join('\n');
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      `TAM SENKRON ONAYI\n\nHesap: ${previewData.email}\n` +
      `Sunucu toplamı: ${previewData.server_total} mail\n` +
      `DB'de mevcut: ${previewData.db_total} mail\n` +
      `Çekilecek (tahmini): ${previewData.estimated_new} mail\n\n` +
      `Klasör detayları:\n${folderLines || '(yok)'}\n\n` +
      `Bu işlem uzun sürebilir (büyük kutu için 10-30 dk). ` +
      `Tamamlandıktan sonra periyodik sync 200 limit ile devam edecek.\n\n` +
      `Devam etmek istiyor musunuz?`
    );
    if (!ok) return;
    try {
      const t = toast.loading('Tam senkron arka planda başlatılıyor...');
      const res = await api.post(`/mail/accounts/${acc.id}/full-sync`);
      toast.dismiss(t);
      if (res.data?.success) {
        toast.success(
          'Tam senkron arka planda başladı — büyük kutular için 10-30 dk sürebilir. ' +
          'İşlem boyunca diğer sayfalar normal çalışır. Mail kutusunu periyodik refresh ile takip edin.',
          { duration: 12000 }
        );
      } else {
        toast.error('Tam senkron başlatılamadı');
      }
      // 10sn sonra listeyi refresh et — sync_in_progress flag'i görmek için
      setTimeout(refresh, 10000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Tam senkron başarısız');
    }
  };

  /** One-click active toggle straight from the account card. Uses the
   * same `/accounts/{id}` PUT with just `{active}` so the backend
   * cascade (deactivate linked rules / report pending reactivation)
   * kicks in exactly as in the full edit form. */
  const toggleActive = async (acc) => {
    try {
      const nextActive = !acc.active;
      const res = await api.put(`/mail/accounts/${acc.id}`, { active: nextActive });
      const paused = res.data?.deactivated_rules || 0;
      const pending = res.data?.pending_reactivation || 0;
      if (nextActive && pending > 0) {
        toast.success('Hesap aktif edildi');
        // eslint-disable-next-line no-alert
        if (window.confirm(`Bu hesaba bağlı ${pending} arşiv kuralı, hesap pasife alındığında otomatik kapatılmıştı.\n\nHepsini yeniden aktif etmek ister misiniz?`)) {
          try {
            const rr = await api.post(`/mail/accounts/${acc.id}/reactivate-rules`);
            toast.success(`${rr.data?.data?.reactivated || 0} kural yeniden aktif edildi`, { duration: 6000 });
          } catch {
            toast.error('Kurallar aktif edilemedi');
          }
        }
      } else if (!nextActive && paused > 0) {
        toast.success('Hesap pasife alındı', {
          description: `${paused} arşiv kuralı otomatik kapatıldı.`,
          duration: 7000,
        });
      } else if (!nextActive) {
        toast.success('Hesap pasife alındı', {
          description: 'Hiçbir kural etkilenmedi — bu hesaba özel bağlı (Hesap alanı bu hesaba ayarlanmış) aktif kural yoktu.',
          duration: 7000,
        });
      } else {
        toast.success(nextActive ? 'Hesap aktif edildi' : 'Hesap pasife alındı');
      }
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Değiştirilemedi');
    }
  };

  return (
    <div className="p-4 max-w-5xl mx-auto space-y-4" data-testid="mail-accounts-page">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <MailIcon className="h-5 w-5 text-emerald-500" /> Mail Hesapları
        </h1>
        <Button size="sm" onClick={onAdd} className="ml-auto bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="mail-account-add">
          <Plus className="h-4 w-4 mr-1" /> Yeni Hesap
        </Button>
        <Button size="sm" variant="outline" onClick={refresh} disabled={loading} data-testid="mail-account-refresh">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>

      <p className="text-xs text-slate-500">
        IMAP hesapları arka planda her N dakikada bir senkron edilir (Inbox + Gönderilmiş + Çöp + Taslaklar).
        Şifreler Fernet ile şifrelenir.
      </p>

      <div className="grid gap-3">
        {accounts.length === 0 && !loading && (
          <Card className="p-8 text-center text-slate-400 text-sm">Henüz hesap eklenmedi.</Card>
        )}
        {accounts.map((a) => (
          <Card key={a.id} className="p-4" data-testid={`mail-account-row-${a.id}`}>
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center shrink-0">
                <MailIcon className="h-5 w-5 text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold">{a.label}</span>
                  <span className="text-xs text-slate-500 truncate">{a.email_address}</span>
                  {a.active ? (
                    <Badge variant="outline" className="text-[10px] text-emerald-700 border-emerald-300">aktif</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] text-slate-500">pasif</Badge>
                  )}
                  <Badge variant="outline" className="text-[10px]">{a.sync_interval_minutes}dk</Badge>
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                  <span>{a.imap_host}:{a.imap_port} {a.imap_ssl ? 'SSL' : 'Plain'}</span>
                  <span>{a.message_count || 0} mesaj</span>
                  {a.last_sync_at && (
                    <span className="inline-flex items-center gap-1">
                      {a.last_sync_status === 'ok' ? <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                        : a.last_sync_status === 'error' ? <AlertCircle className="h-3 w-3 text-red-500" />
                          : <AlertCircle className="h-3 w-3 text-amber-500" />}
                      {new Date(a.last_sync_at).toLocaleString('tr-TR')}
                    </span>
                  )}
                </div>
                {a.last_sync_error && (
                  <div className="text-[11px] text-red-600 mt-1 line-clamp-2">{a.last_sync_error}</div>
                )}
                {/* Şub 10, 2026 — Live progress (sync sırasında klasör bazlı sayım) */}
                {a.sync_in_progress && (
                  <SyncProgressBar
                    currentFolder={a.sync_current_folder}
                    folderStats={a.sync_progress_folders || {}}
                    newSoFar={a.sync_new_so_far || 0}
                    cancelled={!!a.sync_cancel_requested}
                  />
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  className={`h-8 text-[11px] ${a.active ? 'text-amber-700 border-amber-200 hover:bg-amber-50' : 'text-emerald-700 border-emerald-200 hover:bg-emerald-50'}`}
                  onClick={() => toggleActive(a)}
                  data-testid={`mail-account-toggle-${a.id}`}
                  title={a.active ? 'Hesabı pasife al' : 'Hesabı aktif et'}
                >
                  {a.active ? 'Pasife Al' : 'Aktif Et'}
                </Button>
                <Button size="sm" variant="outline" className="h-8" onClick={() => testConn(a)} data-testid={`mail-account-test-${a.id}`}>
                  <TestTube2 className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm" variant="outline" className="h-8"
                  onClick={() => setProgressAccount(a)}
                  data-testid={`mail-account-progress-${a.id}`}
                  title="Senkronizasyon detayı (klasör başına)"
                >
                  <ListTree className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="outline" className="h-8" onClick={() => syncNow(a)} data-testid={`mail-account-sync-${a.id}`} title="Şimdi senkronize et (son 200)" disabled={a.sync_in_progress}>
                  <RefreshCw className={`h-3.5 w-3.5 ${a.sync_in_progress ? 'animate-spin' : ''}`} />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className={`h-8 ${a.initial_sync_completed ? 'border-slate-200' : 'border-emerald-400 text-emerald-700 bg-emerald-50'}`}
                  onClick={() => fullSync(a)}
                  data-testid={`mail-account-fullsync-${a.id}`}
                  disabled={a.sync_in_progress}
                  title={a.initial_sync_completed ? 'Tam senkron (önce özet)' : 'İlk tam senkronizasyon yapılmadı — önerilir'}
                >
                  {a.initial_sync_completed ? 'Tam' : 'İlk Tam Sync'}
                </Button>
                {a.sync_in_progress && (
                  <>
                    <Badge variant="outline" className="h-8 border-amber-400 text-amber-700 bg-amber-50 gap-1 px-2" data-testid={`mail-account-syncing-${a.id}`}>
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Sync…
                    </Badge>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 border-rose-300 text-rose-700 hover:bg-rose-50"
                      onClick={() => cancelSync(a)}
                      data-testid={`mail-account-cancel-${a.id}`}
                      title="Senkronizasyonu durdur"
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </>
                )}
                <Button size="sm" variant="outline" className="h-8" onClick={() => setPermsAccount(a)} title="İzinler" data-testid={`mail-account-perms-${a.id}`}>
                  <UsersIcon className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="outline" className="h-8" onClick={() => onEdit(a)} data-testid={`mail-account-edit-${a.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-red-600 border-red-200 hover:bg-red-50" onClick={() => removeAcc(a)} data-testid={`mail-account-delete-${a.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* ── Add / Edit dialog ───────────────────────────────────────── */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg" data-testid="mail-account-dialog">
          <DialogHeader>
            <DialogTitle>{editId ? 'Hesabı düzenle' : 'Yeni IMAP hesabı'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <Field label="Etiket *">
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="örn. AYTU - Genel" data-testid="mail-form-label" />
            </Field>
            <Field label="E-posta adresi *">
              <Input value={form.email_address} onChange={(e) => setForm({ ...form, email_address: e.target.value })} placeholder="ornek@firma.com" data-testid="mail-form-email" />
            </Field>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Field label="IMAP Host *">
                  <Input value={form.imap_host} onChange={(e) => setForm({ ...form, imap_host: e.target.value })} placeholder="imap.firma.com" data-testid="mail-form-host" />
                </Field>
              </div>
              <Field label="Port">
                <Input type="number" value={form.imap_port} onChange={(e) => setForm({ ...form, imap_port: +e.target.value || 993 })} data-testid="mail-form-port" />
              </Field>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.imap_ssl} onCheckedChange={(v) => setForm({ ...form, imap_ssl: v })} id="ssl" data-testid="mail-form-ssl" />
              <Label htmlFor="ssl" className="text-xs">SSL/TLS</Label>
              <span className="ml-auto" />
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} id="active" data-testid="mail-form-active" />
              <Label htmlFor="active" className="text-xs">Aktif</Label>
            </div>
            <Field label="Kullanıcı adı *">
              <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="genelde e-posta adresi" data-testid="mail-form-username" />
            </Field>
            <Field label={editId ? 'Şifre (boş = değişmez)' : 'Şifre *'}>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="mail-form-password" />
            </Field>
            <Field label="Senkron aralığı (dk)">
              <Input type="number" min={1} value={form.sync_interval_minutes} onChange={(e) => setForm({ ...form, sync_interval_minutes: +e.target.value || 5 })} data-testid="mail-form-interval" />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={busy}>Vazgeç</Button>
            <Button onClick={submit} disabled={busy || !form.label || !form.imap_host || !form.username || (!editId && !form.password)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="mail-form-submit">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Permissions sub-dialog ──────────────────────────────────── */}
      {permsAccount && (
        <PermissionsDialog account={permsAccount} onClose={() => { setPermsAccount(null); refresh(); }} />
      )}
      {progressAccount && (
        <SyncProgressDialog account={progressAccount} onClose={() => { setProgressAccount(null); refresh(); }} />
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] text-slate-500">{label}</Label>
      {children}
    </div>
  );
}

function PermissionsDialog({ account, onClose }) {
  const [perms, setPerms] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const refresh = async () => {
    setLoading(true);
    try {
      const [p, u] = await Promise.all([
        api.get(`/mail/accounts/${account.id}/permissions`),
        // Dedicated endpoint that returns only admin-tier users
        // (SUPER_ADMIN / FULL_ADMIN / FIRM_ADMIN). Regular employees never
        // appear here because they shouldn't read company inboxes.
        api.get('/mail/eligible-users').catch(() => ({ data: { data: [] } })),
      ]);
      setPerms(p.data?.data || []);
      setUsers(u.data?.data || []);
    } catch {
      toast.error('Yüklenemedi');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [account.id]);

  const grant = async (uid) => {
    try {
      await api.post(`/mail/accounts/${account.id}/permissions`, { user_id: uid });
      toast.success('Yetki verildi');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verilemedi');
    }
  };
  const revoke = async (uid) => {
    try {
      await api.delete(`/mail/accounts/${account.id}/permissions/${uid}`);
      toast.success('Yetki kaldırıldı');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kaldırılamadı');
    }
  };

  const granted = new Set(perms.map((p) => p.user_id));
  const filtered = users.filter((u) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (u.full_name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
  });

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="mail-perms-dialog">
        <DialogHeader>
          <DialogTitle>{account.label} — Erişim İzinleri</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500">
          Buradaki kullanıcılar bu hesabın gelen kutusunu Mail sayfasından okuyabilir. Yazma yetkisi yok.
        </p>
        <div className="space-y-2">
          <div className="text-xs font-semibold uppercase text-slate-500">Yetkili kullanıcılar ({perms.length})</div>
          <div className="flex flex-wrap gap-1.5">
            {perms.length === 0 && <span className="text-xs text-slate-400 italic">Henüz yetkili yok</span>}
            {perms.map((p) => (
              <span key={p.user_id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs">
                {p.user?.full_name || p.user_id}
                <button onClick={() => revoke(p.user_id)} className="hover:text-red-600" data-testid={`mail-perm-revoke-${p.user_id}`}>
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
        <div className="space-y-2 pt-3 border-t border-slate-200 dark:border-slate-700">
          <div className="text-xs font-semibold uppercase text-slate-500">Kullanıcı ekle</div>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="ad veya e-posta ara..." className="h-8 text-xs" data-testid="mail-perms-search" />
          <div className="max-h-48 overflow-y-auto space-y-1">
            {loading ? <Loader2 className="h-4 w-4 animate-spin text-slate-400 mx-auto my-4" /> : (
              filtered.slice(0, 30).map((u) => {
                const roleColor = u.role === 'SUPER_ADMIN' ? 'text-rose-700 border-rose-200'
                  : u.role === 'FULL_ADMIN' ? 'text-blue-700 border-blue-200'
                  : 'text-amber-700 border-amber-200';
                const roleLabel = u.role === 'SUPER_ADMIN' ? 'Süper Admin'
                  : u.role === 'FULL_ADMIN' ? 'Full Admin'
                  : 'Firma Admin';
                return (
                  <div key={u.id} className="flex items-center justify-between px-2 py-1 rounded hover:bg-slate-50 dark:hover:bg-slate-800/40">
                    <div className="text-xs">
                      <div className="font-medium flex items-center gap-1.5">
                        {u.full_name || u.email}
                        <Badge variant="outline" className={`text-[8px] px-1 py-0 ${roleColor}`}>{roleLabel}</Badge>
                      </div>
                      <div className="text-[10px] text-slate-500">{u.email}</div>
                    </div>
                    {granted.has(u.id) ? (
                      <Badge variant="outline" className="text-[9px] text-emerald-700">Yetkili</Badge>
                    ) : (
                      <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => grant(u.id)} data-testid={`mail-perm-grant-${u.id}`}>
                        <Plus className="h-3 w-3 mr-0.5" /> İzin Ver
                      </Button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Kapat</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Şub 10, 2026 — Live sync progress UI
 * Backend her klasör bittikten sonra `sync_progress_folders` ve `sync_current_folder`
 * yazar (mail_api._sync_account). 3sn'lik polling sayesinde otomatik tazelenir.
 */
function SyncProgressBar({ currentFolder, folderStats, newSoFar, cancelled }) {
  const folders = Object.entries(folderStats || {}).map(([name, st]) => ({
    name,
    scanned: st?.scanned || 0,
    new: st?.new || 0,
    skipped: st?.skipped || 0,
  }));
  const totalNew = newSoFar || folders.reduce((a, f) => a + f.new, 0);
  return (
    <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px]" data-testid="sync-progress">
      <div className="flex items-center justify-between mb-1.5 gap-2">
        <span className="font-semibold text-amber-800 inline-flex items-center gap-1.5">
          <Loader2 className="h-3 w-3 animate-spin" />
          {cancelled ? 'İptal ediliyor…' : (
            currentFolder ? <>İşleniyor: <span className="font-mono">{currentFolder}</span></> : 'Hazırlanıyor…'
          )}
        </span>
        <span className="text-emerald-700 font-medium">+{totalNew} yeni</span>
      </div>
      {folders.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1">
          {folders.map((f) => (
            <div key={f.name} className="flex items-center gap-2 text-slate-700">
              <CheckCircle2 className="h-3 w-3 text-emerald-500 shrink-0" />
              <span className="font-mono text-[10px] truncate min-w-0 flex-1" title={f.name}>{f.name}</span>
              <span className="text-[10px] text-slate-500 shrink-0">
                {f.scanned} taranan · <span className="text-emerald-600 font-medium">+{f.new}</span> · {f.skipped} atlandı
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



// ── Sync Progress Dialog (Haz 13, 2026) ─────────────────────────────────
// IMAP sunucusundaki klasör tablosu + lokal'de kaç tane var + her satıra
// "Bu Klasörü Senkronla" butonu. 79k+ mail içeren klasörler için kaldığı
// yerden devam — UID-pagination zaten DB'deki min UID'den küçükleri çekiyor.
function SyncProgressDialog({ account, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(null); // folder name

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/mail/accounts/${account.id}/sync-progress`, { timeout: 60000 });
      setData(res.data?.data || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Detay yüklenemedi (IMAP bağlantısı başarısız olabilir)');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // 5sn'de bir light refresh — sync_in_progress true ise stat sayıları güncellenir.
  useEffect(() => {
    if (!data?.sync_in_progress) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [data?.sync_in_progress]);

  const syncFolder = async (folder) => {
    if (data?.sync_in_progress) {
      toast.error('Bu hesap için zaten bir senkron çalışıyor');
      return;
    }
    if (!window.confirm(`"${folder}" klasörü için senkron başlatılsın mı? Kaldığı yerden devam eder.`)) return;
    setSyncing(folder);
    try {
      await api.post(`/mail/accounts/${account.id}/folders/${encodeURIComponent(folder)}/sync`);
      toast.success(`${folder} senkronu arka planda başladı`);
      setTimeout(load, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Senkron başlatılamadı');
    } finally {
      setSyncing(null);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="sync-progress-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListTree className="h-5 w-5 text-indigo-500" />
            Senkronizasyon Detayı — {account.email_address || account.username}
          </DialogTitle>
        </DialogHeader>
        {loading && !data ? (
          <div className="py-10 text-center text-sm text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
            IMAP sunucusu sorgulanıyor (5-10 sn)…
          </div>
        ) : data ? (
          <div className="space-y-3">
            {/* Top summary */}
            <Card className="p-3 bg-indigo-50 dark:bg-indigo-950/30 border-indigo-200">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                <div>
                  <div className="text-[10px] text-slate-500 uppercase">Toplam Klasör</div>
                  <div className="text-lg font-bold">{data.folder_count}</div>
                </div>
                <div>
                  <div className="text-[10px] text-slate-500 uppercase">Sunucu Mail</div>
                  <div className="text-lg font-bold">{data.total_server_messages.toLocaleString('tr-TR')}</div>
                </div>
                <div>
                  <div className="text-[10px] text-slate-500 uppercase">Lokal Mail</div>
                  <div className="text-lg font-bold">{data.total_local_messages.toLocaleString('tr-TR')}</div>
                </div>
                <div>
                  <div className="text-[10px] text-slate-500 uppercase">Tamamlanma</div>
                  <div className={`text-lg font-bold ${data.overall_completion_pct >= 99 ? 'text-emerald-600' : data.overall_completion_pct >= 70 ? 'text-amber-600' : 'text-rose-600'}`}>
                    %{data.overall_completion_pct}
                  </div>
                </div>
              </div>
              {data.total_remaining > 0 && (
                <div className="mt-2 text-[11px] text-center text-amber-700">
                  📦 Kalan: <b>{data.total_remaining.toLocaleString('tr-TR')}</b> mail
                </div>
              )}
              {data.sync_in_progress && (
                <div className="mt-2 text-[11px] text-center text-indigo-700 flex items-center justify-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Aktif senkron: <b>{data.sync_current_folder || '…'}</b> · {data.sync_new_so_far || 0} yeni
                </div>
              )}
            </Card>

            {/* Per-folder table */}
            <div className="border rounded overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 dark:bg-slate-800">
                  <tr>
                    <th className="text-left p-2">Klasör</th>
                    <th className="text-right p-2">Sunucu</th>
                    <th className="text-right p-2">Lokal</th>
                    <th className="text-right p-2">Kalan</th>
                    <th className="text-right p-2">%</th>
                    <th className="text-center p-2">Eylem</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.folders || []).map((f) => (
                    <tr key={f.folder} className="border-t border-slate-200 dark:border-slate-700">
                      <td className="p-2 font-mono truncate max-w-[200px]" title={f.folder}>{f.folder}</td>
                      <td className="p-2 text-right font-mono">{(f.server_total ?? 0).toLocaleString('tr-TR')}</td>
                      <td className="p-2 text-right font-mono">{(f.local_count ?? 0).toLocaleString('tr-TR')}</td>
                      <td className="p-2 text-right font-mono">
                        {f.remaining > 0
                          ? <span className="text-amber-600 font-semibold">{f.remaining.toLocaleString('tr-TR')}</span>
                          : <span className="text-emerald-600">✓</span>}
                      </td>
                      <td className="p-2 text-right">
                        <Badge className={`text-[10px] ${f.completion_pct >= 99 ? 'bg-emerald-100 text-emerald-700' : f.completion_pct >= 70 ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`}>
                          %{f.completion_pct}
                        </Badge>
                      </td>
                      <td className="p-2 text-center">
                        {f.remaining > 0 ? (
                          <Button
                            size="sm" variant="outline"
                            className="h-7 text-[10px] px-2"
                            onClick={() => syncFolder(f.folder)}
                            disabled={syncing === f.folder || data.sync_in_progress}
                            data-testid={`sync-folder-btn-${f.folder.replace(/[^a-z0-9]/gi,'-')}`}
                            title={f.min_uid ? `UID ${f.min_uid}'den daha eskileri çek` : 'Klasörü baştan tarayıp eski mailleri çek'}
                          >
                            {syncing === f.folder
                              ? <Loader2 className="h-3 w-3 animate-spin" />
                              : <><Download className="h-3 w-3 mr-1" />Senkronla</>}
                          </Button>
                        ) : (
                          <span className="text-[10px] text-emerald-600">Tamam</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="text-[10.5px] text-slate-500 space-y-0.5">
              <div>ℹ <b>&quot;Senkronla&quot;</b> butonu klasörün en eski lokal UID&apos;sinden daha eskileri çeker. Tekrar tıklayarak kaldığı yerden devam edebilirsiniz.</div>
              <div>📡 Otomatik sync zaten yeni gelen mailleri çekiyor — bu sayfa SADECE arşivlenmiş eski mailleri toplu indirmek için.</div>
              <div>⏱ 79bin+ maillik klasörler birkaç saat sürebilir; sayfayı kapatabilirsiniz, işlem arka planda devam eder.</div>
            </div>
          </div>
        ) : (
          <div className="py-8 text-center text-sm text-rose-600">
            Veri yüklenemedi. IMAP sunucusu erişilemiyor olabilir.
            <div className="mt-2"><Button size="sm" variant="outline" onClick={load}>Tekrar Dene</Button></div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
            Yenile
          </Button>
          <Button variant="outline" onClick={onClose}>Kapat</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
